import React from 'react';
import { Card, Masonry, Typography } from 'antd';

import {
  DashboardOrg,
  DashboardTicket,
} from '@totem/components/ticketing/ticketDashboard/types';
import TagDisplay, {
  TICKET_PRIORITY_STYLES,
} from '@totem/components/ui_components/widgets/tagDisplay/tagDisplay';

const { Text } = Typography;
const TICKET_URL_BASE = 'https://dashboard.ib-services.net/dashboard/ticket/';

type Props = {
  tickets: DashboardTicket[];
  orgs: Record<string, DashboardOrg>;
};

type HygieneGroup = {
  title: string;
  description: string;
  tickets: DashboardTicket[];
};

const TicketMiniList = ({
  tickets,
  orgs,
}: {
  tickets: DashboardTicket[];
  orgs: Record<string, DashboardOrg>;
}) => {
  if (tickets.length === 0) {
    return (
      <Text type="secondary" style={{ fontSize: 11 }}>
        ✓ Nothing here
      </Text>
    );
  }

  return (
    <div>
      {tickets.slice(0, 20).map((tkt) => (
        <div
          key={tkt.id}
          style={{
            padding: '6px 0',
            borderTop: '1px solid #E5E8EC',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11 }}>
              <a
                href={`${TICKET_URL_BASE}${tkt.id}`}
                target="_blank"
                rel="noreferrer"
              >
                {tkt.num}
              </a>
              {' · '}
              {orgs[tkt.o]?.name ?? tkt.o}
            </div>
            <div style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>
              {tkt.sum}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              alignItems: 'flex-end',
            }}
          >
            <TagDisplay value={tkt.p} config={TICKET_PRIORITY_STYLES} />
            <Text type="secondary" style={{ fontSize: 10 }}>
              {tkt.ageD.toFixed(1)}d
            </Text>
          </div>
        </div>
      ))}
    </div>
  );
};

const HygieneTab = ({ tickets, orgs }: Props) => {
  const sorted = tickets.slice().sort((lhs, rhs) => rhs.u - lhs.u);

  const groups: HygieneGroup[] = [
    {
      title: 'Unowned',
      description: 'No active assignment',
      tickets: sorted.filter((tkt) => tkt.ha === 0),
    },
    {
      title: 'Stale 14–30 days',
      description: 'No update in 14–30 days',
      tickets: sorted.filter((tkt) => tkt.stlD > 14 && tkt.stlD <= 30),
    },
    {
      title: 'Stale 30+ days',
      description: 'No update in over 30 days',
      tickets: sorted.filter((tkt) => tkt.stlD > 30),
    },
    {
      title: 'No Category',
      description: 'Missing category',
      tickets: sorted.filter((tkt) => !tkt.cat),
    },
    {
      title: 'No Building',
      description: 'Not linked to a building',
      tickets: sorted.filter((tkt) => !tkt.b),
    },
    {
      title: 'No Priority',
      description: 'Priority not set',
      tickets: sorted.filter((tkt) => !tkt.p || tkt.p === '—'),
    },
  ];

  const masonryItems = groups.map((group) => ({
    key: group.title,
    data: group,
    children: (
      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {group.title}
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                padding: '1px 8px',
                borderRadius: 999,
                background:
                  group.tickets.length > 0
                    ? 'rgba(201,68,66,0.15)'
                    : 'rgba(47,158,107,0.15)',
                color: group.tickets.length > 0 ? '#C94442' : '#2F9E6B',
              }}
            >
              {group.tickets.length}
            </span>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 400 }}>
              {group.description}
            </Text>
          </span>
        }
        size="small"
        style={{
          borderLeft: `3px solid ${group.tickets.length > 0 ? '#C94442' : '#2F9E6B'}`,
        }}
        styles={{
          header: {
            background:
              group.tickets.length > 0
                ? 'rgba(201,68,66,0.08)'
                : 'rgba(47,158,107,0.08)',
          },
        }}
      >
        <TicketMiniList tickets={group.tickets} orgs={orgs} />
      </Card>
    ),
  }));

  return (
    <Masonry
      items={masonryItems}
      columns={2}
      gutter={16}
      itemRender={(item) => item.children}
    />
  );
};

export default HygieneTab;
